import { useState } from "react";
import { MapPin, Check, Loader2 } from "lucide-react";
import { REGIONS } from "@/lib/regions";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * RegionFilterBar — pill toggle bar for the 5 primary Florida regions.
 *
 * Props:
 *   value:    currently selected region key
 *   onChange: (nextKey) => void
 *   showSaveDefault: bool — when true and value !== user.region, show a "Save as default" link
 */
export default function RegionFilterBar({ value = "all", onChange, showSaveDefault = true }) {
  const { user, refreshMe } = useAuth();
  const [saving, setSaving] = useState(false);

  const savedRegion = user?.region || "all";
  const differsFromSaved = savedRegion !== value;

  const saveAsDefault = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me", { region: value });
      if (refreshMe) await refreshMe();
      toast.success(`Saved — ${REGIONS.find((r) => r.key === value)?.label} is now your default`);
    } catch {
      toast.error("Couldn't save your region — try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="region-filter-bar">
      <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">
        <MapPin className="w-3.5 h-3.5 text-amber-500" /> Region
      </div>
      <div className="inline-flex items-center gap-1 rounded-full bg-white border border-stone-200 p-1 flex-wrap">
        {REGIONS.map((r) => {
          const active = value === r.key;
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => onChange?.(r.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                active
                  ? "bg-amber-500 text-white shadow-sm"
                  : "text-stone-600 hover:bg-stone-100"
              }`}
              data-testid={`region-pill-${r.key}`}
            >
              {r.short}
            </button>
          );
        })}
      </div>

      {showSaveDefault && differsFromSaved && (
        <button
          type="button"
          onClick={saveAsDefault}
          disabled={saving}
          className="text-xs text-amber-700 hover:text-amber-900 underline underline-offset-2 font-medium disabled:opacity-50 disabled:no-underline"
          data-testid="region-save-default"
        >
          {saving ? (
            <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
          ) : (
            <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" /> Save as my default</span>
          )}
        </button>
      )}
      {showSaveDefault && !differsFromSaved && value !== "all" && (
        <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1" data-testid="region-is-default">
          <Check className="w-3 h-3" /> Default
        </span>
      )}
    </div>
  );
}
